/**
 * 全局输入弹窗 —— 替代阻塞、风格割裂的 window.prompt,视觉跟应用主题一致。
 *
 * 用法:
 *   import { usePrompt } from '@/composables/usePrompt'
 *   const { prompt } = usePrompt()
 *   const reason = await prompt({ title: '作废出货单', message: '...', placeholder: '作废原因(可留空)' })
 *   if (reason === null) return        // 取消(按钮 / 遮罩 / Esc)
 *   // reason 为字符串(可能空串)→ 确认
 *
 * 语义跟 window.prompt 对齐:确认 → 返回输入字符串(可空);取消 → null。
 * 实现:模块级 reactive 单实例 + 容器组件 (PromptDialog.vue) 在 AppLayout 挂一次,
 * 跟 useConfirm / useToast 同形。
 */
import { reactive } from 'vue'

export interface PromptOptions {
  /** 标题,默认「请输入」 */
  title?: string
  /** 正文(输入框上方说明),支持 \n 换行 */
  message?: string
  /** 输入框默认值 */
  defaultValue?: string
  /** 输入框 placeholder */
  placeholder?: string
  /** 输入类型:text(默认) / number */
  inputType?: 'text' | 'number'
  /** 确认按钮文案,默认「确定」 */
  confirmText?: string
  /** 取消按钮文案,默认「取消」 */
  cancelText?: string
  /** 危险操作 → 确认按钮标红 */
  danger?: boolean
}

interface PromptState {
  open: boolean
  title: string
  message: string
  value: string
  placeholder: string
  inputType: 'text' | 'number'
  confirmText: string
  cancelText: string
  danger: boolean
}

const state: PromptState = reactive({
  open: false,
  title: '请输入',
  message: '',
  value: '',
  placeholder: '',
  inputType: 'text',
  confirmText: '确定',
  cancelText: '取消',
  danger: false
})

let resolver: ((val: string | null) => void) | null = null

function prompt(opts: PromptOptions): Promise<string | null> {
  // 上一个还没结算就又弹(理论上不该发生)→ 先把旧的当取消结算,避免 await 永远挂着。
  if (resolver) {
    resolver(null)
    resolver = null
  }
  state.title = opts.title ?? '请输入'
  state.message = opts.message ?? ''
  state.value = opts.defaultValue ?? ''
  state.placeholder = opts.placeholder ?? ''
  state.inputType = opts.inputType ?? 'text'
  state.confirmText = opts.confirmText ?? '确定'
  state.cancelText = opts.cancelText ?? '取消'
  state.danger = opts.danger ?? false
  state.open = true
  return new Promise<string | null>((resolve) => {
    resolver = resolve
  })
}

/** 容器组件结算:确认传当前输入串,取消传 null。 */
function settle(val: string | null) {
  if (!state.open) return
  state.open = false
  const r = resolver
  resolver = null
  r?.(val)
}

export function usePrompt() {
  return {
    /** 内部 reactive state,容器组件 PromptDialog 用 */
    state,
    /** 弹输入框,返回 Promise<string | null>(确认=字符串可空 / 取消=null) */
    prompt,
    /** 容器组件确认(传字符串)/取消(传 null)时调用 */
    _settle: settle
  }
}
